"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProportionalConfidencePolicy = exports.GradedConfidencePolicy = exports.UnanimousConfidencePolicy = exports.ConfidencePolicy = void 0;
class ConfidencePolicy {
    static { this.defaultMinReviewers = 2; }
    constructor(minReviewers = ConfidencePolicy.defaultMinReviewers) {
        if (!Number.isInteger(minReviewers) || minReviewers < 1) {
            throw new RangeError(`ConfidencePolicy minReviewers must be an integer >= 1, but received ${minReviewers}.`);
        }
        this.minReviewers = minReviewers;
    }
    label(tally, totalReviewers) {
        const belowQuorum = totalReviewers < this.minReviewers;
        const subQuorumScore = {
            confidence: 0,
            confidenceRationale: `Only ${totalReviewers} reviewer(s); at least ${this.minReviewers} are required for consensus.`,
        };
        return {
            anchor: tally.anchor,
            candidates: tally.candidates.map((candidate) => ({
                ...candidate,
                ...(belowQuorum ? subQuorumScore : this.scoreCandidate(candidate, totalReviewers)),
                denominator: totalReviewers,
            })),
        };
    }
    formatShare(voteCount, totalReviewers) {
        return `${voteCount} of ${totalReviewers} reviewer(s)`;
    }
}
exports.ConfidencePolicy = ConfidencePolicy;
class UnanimousConfidencePolicy extends ConfidencePolicy {
    constructor(options = {}) {
        super(options.minReviewers);
    }
    scoreCandidate(candidate, totalReviewers) {
        if (candidate.voteCount === totalReviewers) {
            return { confidence: 1, confidenceRationale: `All ${totalReviewers} reviewers proposed this value.` };
        }
        const share = this.formatShare(candidate.voteCount, totalReviewers);
        return { confidence: 0, confidenceRationale: `Only ${share} proposed this; unanimity required.` };
    }
}
exports.UnanimousConfidencePolicy = UnanimousConfidencePolicy;
class GradedConfidencePolicy extends ConfidencePolicy {
    static { this.defaultMajorityConfidence = 0.7; }
    constructor(options = {}) {
        super(options.minReviewers);
        const majorityConfidence = options.majorityConfidence ?? GradedConfidencePolicy.defaultMajorityConfidence;
        if (!Number.isFinite(majorityConfidence) || majorityConfidence < 0 || majorityConfidence > 1) {
            throw new RangeError(`GradedConfidencePolicy majorityConfidence must be a finite number within [0, 1], ` +
                `but received ${majorityConfidence}.`);
        }
        this.majorityConfidence = majorityConfidence;
    }
    scoreCandidate(candidate, totalReviewers) {
        const ratio = candidate.voteCount / totalReviewers;
        if (ratio === 1) {
            return { confidence: 1, confidenceRationale: `All ${totalReviewers} reviewers agreed.` };
        }
        const share = this.formatShare(candidate.voteCount, totalReviewers);
        if (ratio > 0.5) {
            const percent = Math.round(this.majorityConfidence * 100);
            return {
                confidence: this.majorityConfidence,
                confidenceRationale: `A majority (${share}) chose this; held at ${percent}% until unanimous.`,
            };
        }
        return { confidence: 0, confidenceRationale: `Only ${share} chose this (minority).` };
    }
}
exports.GradedConfidencePolicy = GradedConfidencePolicy;
class ProportionalConfidencePolicy extends ConfidencePolicy {
    constructor(options = {}) {
        super(options.minReviewers);
    }
    scoreCandidate(candidate, totalReviewers) {
        const share = this.formatShare(candidate.voteCount, totalReviewers);
        const percent = Math.round((candidate.voteCount / totalReviewers) * 100);
        return {
            confidence: candidate.voteCount / totalReviewers,
            confidenceRationale: `${share} chose this value (${percent}%).`,
        };
    }
}
exports.ProportionalConfidencePolicy = ProportionalConfidencePolicy;
//# sourceMappingURL=confidence-policy.js.map